import apiClient from '../../../config/api';
import type { LaunchExchangeOutcome, LaunchSession, LaunchValidateResponse } from '../types';

/**
 * Launch service — Pillar C client.
 *
 * Targets the Ainovis-One backend launch routes (`/api/v1/launch/*`),
 * which proxy to the Hub canonical endpoints (Hub Phase 8a + AO Phase 9).
 *
 * Only `validateLaunchToken` is exposed: the frontend never talks to
 * study-context / priors / delivery directly from this layer (those flows
 * live in the editor when the launch is finalized).
 */
class LaunchService {
  /**
   * Validate a launch token (`POST /api/v1/launch/validate`).
   *
   * Called when the radiologist is redirected from a PACS or RIS worklist
   * to `/launch?token=…`. The backend forwards to the Hub, which decodes
   * the JWT, hits the gateway and returns `studyContext` + `patientHistory`.
   */
  async validateLaunchToken(token: string): Promise<LaunchValidateResponse> {
    const response = await apiClient.post('/launch/validate', { token });
    return response.data;
  }

  /**
   * Canjea un launch token por una sesión (`POST /api/v1/auth/launch-exchange`).
   *
   * Se llama cuando el radiólogo llega desde el visor **sin sesión abierta**.
   * Si el lanzamiento traía una identidad verificada y esa identidad
   * corresponde a un usuario de la cuenta, el backend devuelve una sesión y el
   * profesional continúa sin pasar por el formulario.
   *
   * **Nunca lanza.** Devolver `null` es la respuesta normal, no una anomalía:
   * la inmensa mayoría de los lanzamientos de hoy no traen identidad. Y ante un
   * fallo de red o un backend caído, la única salida correcta sigue siendo el
   * formulario de acceso con el contexto intacto, nunca una pantalla de error
   * que deje al radiólogo sin camino.
   *
   * No se registra nada aquí. El token es de un solo uso y credencial de pleno
   * derecho mientras vive; anotarlo en la consola del navegador lo dejaría a la
   * vista de cualquiera con la pestaña de desarrollo abierta.
   */
  async exchangeForSession(token: string): Promise<LaunchExchangeOutcome> {
    try {
      const response = await apiClient.post<{
        authenticated?: boolean;
        identityRefused?: boolean;
        token?: string;
        user?: LaunchSession['user'];
      }>('/auth/launch-exchange', { token });

      const body = response.data;
      // Se exigen las tres cosas. Una respuesta a medias no es media sesión:
      // es una sesión que no se puede construir, y se trata como un rechazo.
      if (!body?.authenticated || !body.token || !body.user) {
        // El backend distingue una sola cosa, y no es el motivo: si el
        // lanzamiento traía identidad. Con ella rechazada hay otra persona al
        // otro lado, y seguir con la sesión que hubiera abierta en el equipo
        // sería atenderla como si fuera quien no es.
        return { status: body?.identityRefused ? 'refused' : 'no-identity' };
      }

      return { status: 'session', session: { token: body.token, user: body.user } };
    } catch {
      // Una red caída no dice nada sobre quién lanza. Se trata como el caso
      // corriente: no hay identidad que aplicar, y el camino de siempre sigue
      // disponible.
      return { status: 'no-identity' };
    }
  }
}

const launchService = new LaunchService();
export default launchService;
